import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import moment from 'moment';
import { BpStatementPdfInput } from './invoicePdfTypes';
import { formatMoney } from './format';

const styles = StyleSheet.create({
    page: { padding: 36, fontSize: 10, fontFamily: 'Helvetica', color: '#1f2937' },
    title: { fontSize: 20, fontWeight: 700, color: '#008ac9' },
    label: { fontSize: 8, color: '#6b7280' },
    kpiRow: { flexDirection: 'row', gap: 8, marginTop: 14, marginBottom: 14 },
    kpi: { flex: 1, borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 4, padding: 8 },
    kpiValue: { fontSize: 12, fontWeight: 700, marginTop: 4 },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#f3f4f6',
        padding: 6,
        fontWeight: 700,
        fontSize: 8
    },
    tableRow: { flexDirection: 'row', padding: 6, borderBottomWidth: 1, borderColor: '#f1f3f5', fontSize: 9 }
});

const BpStatementPdfDocument = ({ businessPartner, accounting, issuer, statementDate, accountNumber }: BpStatementPdfInput) => {
    const { kpis, rows, ledgerEntries = [] } = accounting;
    const cur = kpis.currency;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <View>
                        <Text style={{ fontSize: 13, fontWeight: 700, color: '#008ac9' }}>{issuer.name}</Text>
                        {issuer.address ? <Text>{issuer.address}</Text> : null}
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                        <Text style={styles.title}>STATEMENT</Text>
                        <Text style={styles.label}>Statement date</Text>
                        <Text>{moment(statementDate).format('DD MMM YYYY')}</Text>
                        <Text style={styles.label}>Account</Text>
                        <Text style={{ color: '#008ac9' }}>{accountNumber}</Text>
                        {kpis.paymentTermsLabel ? (
                            <>
                                <Text style={styles.label}>Payment terms</Text>
                                <Text>{kpis.paymentTermsLabel}</Text>
                            </>
                        ) : null}
                    </View>
                </View>

                <Text style={{ marginTop: 12, fontWeight: 700 }}>Bill to: {businessPartner.name}</Text>

                <View style={styles.kpiRow}>
                    <View style={styles.kpi}>
                        <Text style={styles.label}>Open balance</Text>
                        <Text style={[styles.kpiValue, { color: '#b91c1c' }]}>
                            {formatMoney(kpis.totalOpenBalance, cur)}
                        </Text>
                    </View>
                    <View style={styles.kpi}>
                        <Text style={styles.label}>Overdue</Text>
                        <Text style={styles.kpiValue}>{formatMoney(kpis.overdueAmount, cur)}</Text>
                    </View>
                    <View style={styles.kpi}>
                        <Text style={styles.label}>Paid (60 days)</Text>
                        <Text style={styles.kpiValue}>{formatMoney(kpis.paidLast60, cur)}</Text>
                    </View>
                    <View style={styles.kpi}>
                        <Text style={styles.label}>Available credit</Text>
                        <Text style={[styles.kpiValue, { color: '#008ac9' }]}>
                            {formatMoney(kpis.availableCredit, cur)}
                        </Text>
                    </View>
                </View>
                <Text style={styles.label}>Credit line: {formatMoney(kpis.creditLine, cur)}</Text>

                <Text style={{ marginTop: 16, fontWeight: 700, fontSize: 9, color: '#6b7280' }}>SHIPMENT BALANCES</Text>
                <View style={{ marginTop: 6 }}>
                    <View style={styles.tableHeader}>
                        <Text style={{ width: '22%' }}>Shipment</Text>
                        <Text style={{ width: '14%' }}>Date</Text>
                        <Text style={{ width: '16%', textAlign: 'right' }}>Total</Text>
                        <Text style={{ width: '14%', textAlign: 'right' }}>Paid</Text>
                        <Text style={{ width: '16%', textAlign: 'right' }}>Open</Text>
                        <Text style={{ width: '18%' }}>Due</Text>
                    </View>
                    {rows.slice(0, 40).map((r) => (
                        <View style={styles.tableRow} key={r.shipmentId}>
                            <Text style={{ width: '22%' }}>{r.shipmentNumber}</Text>
                            <Text style={{ width: '14%' }}>
                                {r.shipmentDate ? moment(r.shipmentDate).format('DD MMM YY') : '—'}
                            </Text>
                            <Text style={{ width: '16%', textAlign: 'right' }}>
                                {formatMoney(r.totalCost, r.currency)}
                            </Text>
                            <Text style={{ width: '14%', textAlign: 'right' }}>{formatMoney(r.paid, r.currency)}</Text>
                            <Text style={{ width: '16%', textAlign: 'right' }}>{formatMoney(r.openBalance, r.currency)}</Text>
                            <Text style={{ width: '18%' }}>
                                {r.dueDate ? moment(r.dueDate).format('DD MMM YY') : '—'}
                            </Text>
                        </View>
                    ))}
                </View>

                {ledgerEntries.length > 0 && (
                    <>
                        <Text style={{ marginTop: 16, fontWeight: 700, fontSize: 9, color: '#6b7280' }}>
                            ACCOUNT LEDGER
                        </Text>
                        <View style={{ marginTop: 6 }}>
                            <View style={styles.tableHeader}>
                                <Text style={{ width: '14%' }}>Date</Text>
                                <Text style={{ width: '16%' }}>Type</Text>
                                <Text style={{ width: '18%' }}>Document</Text>
                                <Text style={{ width: '18%' }}>Shipment</Text>
                                <Text style={{ width: '16%', textAlign: 'right' }}>Amount</Text>
                                <Text style={{ width: '18%' }}>By</Text>
                            </View>
                            {ledgerEntries.slice(0, 50).map((e) => (
                                <View style={styles.tableRow} key={e.id}>
                                    <Text style={{ width: '14%' }}>
                                        {moment(e.createdAt).format('DD MMM YY')}
                                    </Text>
                                    <Text style={{ width: '16%' }}>{e.entryType.replace('_', ' ')}</Text>
                                    <Text style={{ width: '18%' }}>{e.documentNumber || '—'}</Text>
                                    <Text style={{ width: '18%' }}>{e.shipmentNumber || '—'}</Text>
                                    <Text style={{ width: '16%', textAlign: 'right' }}>
                                        {formatMoney(e.amount, e.currency)}
                                    </Text>
                                    <Text style={{ width: '18%' }}>{e.userName || '—'}</Text>
                                </View>
                            ))}
                        </View>
                    </>
                )}
            </Page>
        </Document>
    );
};

export default BpStatementPdfDocument;
